import { useEffect, useState } from 'react';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

type TaskStatus = 'processing' | 'completed' | 'failed' | 'cancelled' | 'open';

interface ScaffolderTask {
  id: string;
  status: TaskStatus;
  createdAt: string;
  spec: {
    templateInfo?: { entity?: { metadata?: { title?: string; name?: string } } };
    parameters?: Record<string, unknown>;
  };
}

const statusColor = (s: TaskStatus): 'default' | 'primary' | 'secondary' => {
  if (s === 'completed') return 'primary';
  if (s === 'failed') return 'secondary';
  return 'default';
};

const useStyles = makeStyles(theme => ({
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(3) },
  empty: { padding: theme.spacing(2), color: theme.palette.text.secondary, textAlign: 'center' },
  chip: { height: 18, fontSize: '0.65rem' },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(10),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
}));

const ago = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const RecentActivityCard = ({ userEntityRef }: { userEntityRef?: string }) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tasks, setTasks] = useState<ScaffolderTask[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userEntityRef) return;
    setLoading(true);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const resp = await fetchApi.fetch(
          `${baseUrl}/v2/tasks?createdBy=${encodeURIComponent(userEntityRef)}&order=${encodeURIComponent('desc:created_at')}&limit=5`,
        );
        if (resp.ok) {
          const data = await resp.json();
          setTasks((data.tasks ?? []).slice(0, 5));
        }
      } catch {
        // non-fatal
      } finally {
        setLoading(false);
      }
    })();
  }, [discoveryApi, fetchApi, userEntityRef]);

  const taskName = (t: ScaffolderTask) =>
    t.spec?.templateInfo?.entity?.metadata?.title ??
    t.spec?.templateInfo?.entity?.metadata?.name ??
    t.id.slice(0, 8);

  return (
    <InfoCard title="Recent Activity" subheader="Your last 5 scaffolder runs">
      {(loading || !userEntityRef) ? (
        <div className={classes.loading}><CircularProgress size={24} /></div>
      ) : null}
      {!loading && userEntityRef && tasks.length === 0 ? (
        <Typography className={classes.empty}>
          No runs yet.{' '}<Link to="/create">Start a template</Link> to see activity here.
        </Typography>
      ) : null}
      {!loading && userEntityRef && tasks.length > 0 ? (
        <List dense disablePadding>
          {tasks.map(t => (
            <ListItem
              key={t.id}
              button
              component="a"
              href={`/create/tasks/${t.id}`}
              className={classes.listItem}
            >
              <ListItemText
                primary={taskName(t)}
                secondary={ago(t.createdAt)}
              />
              <ListItemSecondaryAction>
                <Chip
                  label={t.status}
                  size="small"
                  color={statusColor(t.status)}
                  className={classes.chip}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : null}
    </InfoCard>
  );
};
